import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAuthHeader } from "../context/AuthContext";
import { POSLayout } from "@/components/POSLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  DollarSign,
  Printer,
  Save,
  Send,
  Search,
  Bell,
  User,
  X,
  Utensils,
  Edit2,
  QrCode,
  Filter,
  Clock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  Menu,
  LayoutGrid,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import ModifierDialog from "@/components/ModifierDialog";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function POSScreen() {
  const navigate = useNavigate();
  const [restaurantId, setRestaurantId] = useState(null);
  const [tables, setTables] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [orderType, setOrderType] = useState("dine_in");
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [discount, setDiscount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountType, setDiscountType] = useState("percentage");
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedQrTable, setSelectedQrTable] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [serverName, setServerName] = useState("Admin User");
  const [orderTime, setOrderTime] = useState(new Date());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedItemForNote, setSelectedItemForNote] = useState(null);
  const [itemNote, setItemNote] = useState("");
  const [existingOrderId, setExistingOrderId] = useState(null);
  const [selectedSection, setSelectedSection] = useState("all");
  const [tableSearchTerm, setTableSearchTerm] = useState("");
  const [modifierDialogOpen, setModifierDialogOpen] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [printerStatus, setPrinterStatus] = useState(null);
  const [reprintingKot, setReprintingKot] = useState(false);
  const [reprintingReceipt, setReprintingReceipt] = useState(false);
  const [itemNoteForModifier, setItemNoteForModifier] = useState("");
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState("all");
  const [orderPaymentStatus, setOrderPaymentStatus] = useState("pending");
  const [splitBillOrders, setSplitBillOrders] = useState([]);
  const [currentSplitOrderIndex, setCurrentSplitOrderIndex] = useState(0);
  const [isSplitBill, setIsSplitBill] = useState(false);
  const [tablesWithSplitBills, setTablesWithSplitBills] = useState(new Set());

  // Mobile responsiveness states
  const [activePanel, setActivePanel] = useState('menu'); // 'tables', 'menu', 'cart'
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Two-level navigation: Top level = different orders, Second level = split persons within an order
  const [allTableOrders, setAllTableOrders] = useState([]); // All orders for a table
  const [currentOrderIndex, setCurrentOrderIndex] = useState(0); // Which order we're viewing
  const [currentSplitPersonIndex, setCurrentSplitPersonIndex] = useState(0); // Which person in split bill
  const [activeOrders, setActiveOrders] = useState([]);
  const [activeOrdersLoading, setActiveOrdersLoading] = useState(false);

  // Fetch restaurant ID
  useEffect(() => {
    const fetchRestaurantId = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: getAuthHeader(),
        });
        const restoId = response.data.restaurant_id;
        setRestaurantId(restoId);
        console.log("Restaurant ID:", restoId);
      } catch (error) {
        console.error("Error fetching restaurant ID:", error);
      }
    };
    fetchRestaurantId();
  }, []);

  useEffect(() => {
    fetchTables();
    fetchMenuItems();
    fetchCategories();
    fetchSections();
    fetchPrinterStatus();

    // Initialize WebSocket notifications after a short delay to ensure script is loaded
    setTimeout(() => {
      if (window.posNotifications && restaurantId) {
        console.log(
          "Connecting to WebSocket with restaurant ID:",
          restaurantId
        );
        window.posNotifications.connect(restaurantId);

        // Override the notification handler to respect our enabled state
        const originalShowPopup = window.posNotifications.showPopup.bind(
          window.posNotifications
        );
        const originalAnnounce = window.posNotifications.announceOrder.bind(
          window.posNotifications
        );

        window.posNotifications.showPopup = function (data) {
          if (notificationsEnabled) {
            setNotificationCount((prev) => prev + 1);
            originalShowPopup(data);
          }
        };

        window.posNotifications.announceOrder = function (message) {
          if (notificationsEnabled) {
            originalAnnounce(message);
          }
        };
      }
    }, 1000);

    // Auto-refresh tables every 30 seconds to show real-time status
    const tablesRefreshInterval = setInterval(() => {
      fetchTables();
    }, 30000); // 30 seconds

    // Auto-refresh printer status every 30 seconds
    const printerRefreshInterval = setInterval(() => {
      fetchPrinterStatus();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(tablesRefreshInterval);
      clearInterval(printerRefreshInterval);
      if (window.posNotifications) {
        window.posNotifications.disconnect();
      }
    };
  }, [restaurantId]);

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime(Math.floor((new Date() - orderTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [orderTime]);

  useEffect(() => {
    if (selectedTable && orderType === "dine_in") {
      // Always fetch orders for the selected table
      // This ensures cart is updated when switching between tables
      fetchTableOrders(selectedTable.id);
    } else if (!selectedTable) {
      // Clear cart when no table is selected
      setCart([]);
      setExistingOrderId(null);
      setOrderPaymentStatus("pending");
    }
  }, [selectedTable]);

  useEffect(() => {
    fetchActiveOrders();
  }, [orderType]);


  const fetchTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`, {
        headers: getAuthHeader(),
      });
      setTables(response.data);
    } catch (error) {
      console.error("Error fetching tables:", error);
    }
  };

  const fetchActiveOrders = async () => {
    try {
      setActiveOrdersLoading(true);

      const response = await axios.get(
        `${API}/orders/active`,
        {
          headers: getAuthHeader(),
          params: {
            order_type: orderType !== "dine_in" ? orderType : undefined,
          },
        }
      );

      setActiveOrders(response.data || []);
    } catch (err) {
      console.error("Failed to fetch active orders", err);
    } finally {
      setActiveOrdersLoading(false);
    }
  };


  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(
        `${API}/menu/items?available_only=true`,
        { headers: getAuthHeader() }
      );
      setMenuItems(response.data);
    } catch (error) {
      console.error("Error fetching menu:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: getAuthHeader(),
      });
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API}/sections`, {
        headers: getAuthHeader(),
      });
      setSections(response.data);
    } catch (error) {
      console.error("Error fetching sections:", error);
      setSections([]);
    }
  };

  const fetchPrinterStatus = async () => {
    try {
      const response = await axios.get(`${API}/printers/status`, {
        headers: getAuthHeader(),
      });
      setPrinterStatus(response.data);
    } catch (error) {
      console.error("Error fetching printer status:", error);
      setPrinterStatus(null);
    }
  };

  const handleReprintKot = async (orderId) => {
    try {
      setReprintingKot(true);
      await axios.post(
        `${API}/printers/reprint/kot/${orderId}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("KOT reprinted successfully!");
    } catch (error) {
      console.error("Error reprinting KOT:", error);
      toast.error(error.response?.data?.detail || "Failed to reprint KOT");
    } finally {
      setReprintingKot(false);
    }
  };

  const handleReprintReceipt = async (orderId) => {
    try {
      setReprintingReceipt(true);
      await axios.post(
        `${API}/printers/reprint/receipt/${orderId}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Receipt reprinted successfully!");
    } catch (error) {
      console.error("Error reprinting receipt:", error);
      toast.error(error.response?.data?.detail || "Failed to reprint receipt");
    } finally {
      setReprintingReceipt(false);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await axios.get(
        `${API}/subcategories?category_id=${categoryId}`,
        { headers: getAuthHeader() }
      );
      setSubCategories(response.data);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setSubCategories([]);
    }
  };

  // Load subcategories when category changes
  useEffect(() => {
    if (selectedCategory !== "all") {
      fetchSubCategories(selectedCategory);
      setSelectedSubCategory("all");
    } else {
      setSubCategories([]);
      setSelectedSubCategory("all");
    }
  }, [selectedCategory]);

  const fetchTableOrders = async (tableId) => {
    try {
      const response = await axios.get(
        `${API}/orders?table_id=${tableId}&status=pending`,
        {
          headers: getAuthHeader(),
        }
      );

      // Actual data array
      const orders = response.data.data;

      console.log("API Response:", response.data); // full response object
      console.log("Orders array:", orders); // the actual array

      if (orders && orders.length > 0) {
        // Group orders by parent_order_id or order id
        const orderGroupsMap = new Map();

        orders.forEach((order) => {
          const key = order.parent_order_id || order.id; // use parent_order_id if exists
          if (!orderGroupsMap.has(key)) {
            orderGroupsMap.set(key, []);
          }
          orderGroupsMap.get(key).push(order);
        });

        const orderGroups = Array.from(orderGroupsMap.values()).map(
          (group) => ({
            isSplitBill: group.length > 1,
            orders: group,
            parentOrderId: group[0].parent_order_id || null,
          })
        );

        setAllTableOrders(orderGroups);
        setCurrentOrderIndex(0);
        setCurrentSplitPersonIndex(0);

        // Mark tables with split bills
        if (orderGroups.length > 1 || orderGroups.some((g) => g.isSplitBill)) {
          setTablesWithSplitBills((prev) => new Set(prev).add(tableId));
        } else {
          setTablesWithSplitBills((prev) => {
            const newSet = new Set(prev);
            newSet.delete(tableId);
            return newSet;
          });
        }

        // Load first order
        loadOrderGroup(orderGroups[0], 0);

        console.log(
          `✅ Loaded ${orderGroups.length} order group(s) for table ${tableId}`
        );
      } else {
        console.log(
          `📋 No existing orders for table ${tableId}, clearing cart`
        );
        clearOrderState(tableId);
      }
    } catch (error) {
      console.error("Error fetching table orders:", error);
      clearOrderState(tableId);
    }
  };

  const loadOrderGroup = (orderGroup, groupIndex) => {
    if (!orderGroup || !orderGroup.orders || orderGroup.orders.length === 0)
      return;

    const firstOrder =
      orderGroup.orders.find((o) => o.items && o.items.length > 0) ||
      orderGroup.orders[0];

    const items = firstOrder.items || [];

    if (orderGroup.isSplitBill) {
      setSplitBillOrders(orderGroup.orders);
      setIsSplitBill(true);
      setCurrentSplitPersonIndex(0);
    } else {
      setSplitBillOrders([]);
      setIsSplitBill(false);
      setCurrentSplitPersonIndex(0);
    }

    setCart(items);
    setCustomerName(
      firstOrder.customer_name || firstOrder.split_person_name || ""
    );
    setCustomerPhone(
      firstOrder.customer_phone || firstOrder.split_person_phone || ""
    );
    setExistingOrderId(firstOrder.id);
    setOrderPaymentStatus(firstOrder.payment_status || "pending");

    console.log("🛒 Cart loaded with items:", items);
  };

  const loadSingleOrder = (order, tableId) => {
    // This function is no longer needed but kept for compatibility
    // Use loadOrderGroup instead
    setAllTableOrders([
      {
        isSplitBill: false,
        orders: [order],
        parentOrderId: null,
      },
    ]);
    setCurrentOrderIndex(0);
    setCurrentSplitPersonIndex(0);

    setCart(order.items || []);
    setCustomerName(order.customer_name || "");
    setCustomerPhone(order.customer_phone || "");
    setExistingOrderId(order.id);
    setOrderPaymentStatus(order.payment_status || "pending");
    setIsSplitBill(false);
    setSplitBillOrders([]);

    // Remove table from multiple orders indicator
    setTablesWithSplitBills((prev) => {
      const newSet = new Set(prev);
      newSet.delete(tableId);
      return newSet;
    });

    console.log(
      `✅ Loaded existing order for table ${tableId}:`,
      order.id,
      `with ${order.items?.length || 0} items`
    );
    toast.info(`Loaded existing order with ${order.items?.length || 0} items`);
  };

  const clearOrderState = (tableId) => {
    setCart([]);
    setCustomerName("");
    setCustomerPhone("");
    setExistingOrderId(null);
    setOrderPaymentStatus("pending");
    setIsSplitBill(false);
    setSplitBillOrders([]);
    setCurrentSplitOrderIndex(0);
    setAllTableOrders([]);
    setCurrentOrderIndex(0);
    setCurrentSplitPersonIndex(0);

    // Remove table from multiple orders indicator
    setTablesWithSplitBills((prev) => {
      const newSet = new Set(prev);
      newSet.delete(tableId);
      return newSet;
    });
  };

  const getTableStatusColor = (status) => {
    switch (status) {
      case "available":
        return "bg-green-500 text-white border-green-600";
      case "occupied":
        return "bg-yellow-500 text-black border-yellow-600";
      case "reserved":
        return "bg-red-500 text-white border-red-600";
      default:
        return "bg-gray-400 text-white border-gray-500";
    }
  };

  const addToCart = (item) => {
    // Check if customer details are required for takeaway/delivery
    if (
      (orderType === "takeaway" || orderType === "delivery") &&
      (!customerName || !customerPhone)
    ) {
      toast.error("Please enter customer details before adding items");
      setCustomerOpen(true);
      return;
    }

    // Check if item has modifiers or add-ons
    if (
      (item.modifier_groups && item.modifier_groups.length > 0) ||
      (item.add_ons && item.add_ons.length > 0)
    ) {
      // Open modifier dialog
      setSelectedMenuItem(item);
      setSelectedModifiers({});
      setSelectedAddOns([]);
      setItemNoteForModifier("");
      setModifierDialogOpen(true);
      return;
    }

    // No modifiers, add directly to cart
    const price =
      orderType === "dine_in"
        ? item.pricing?.dine_in
        : orderType === "takeaway"
        ? item.pricing?.takeaway
        : item.pricing?.delivery || item.price;

    const existing = cart.find(
      (c) => c.item_id === item.id && !c.modifiers && !c.add_ons
    );
    if (existing) {
      setCart(
        cart.map((c) =>
          c.item_id === item.id && !c.modifiers && !c.add_ons
            ? { ...c, quantity: c.quantity + 1 }
            : c
        )
      );
    } else {
      setCart([
        ...cart,
        {
          item_id: item.id,
          item_name: item.name,
          quantity: 1,
          price: price,
          tax: item.tax || 0,
          notes: "",
          modifiers: [],
          add_ons: [],
        },
      ]);
    }
    toast.success(`${item.name} added`);
  };

  const addToCartWithModifiers = () => {
    if (!selectedMenuItem) return;

    // Check if item has modifiers or add-ons
    const hasRequiredModifier = selectedMenuItem.modifier_groups?.some(
      (group) =>
        group.required && (!selectedModifiers || !selectedModifiers[group.name])
    );

    const hasRequiredAddon =
      selectedMenuItem.add_ons?.length > 0 &&
      (!selectedAddOns || selectedAddOns.length === 0);

    if (hasRequiredModifier) {
      toast.error("Please select required Modifier");
      setCustomerOpen(true);
      return;
    }

    if (hasRequiredAddon) {
      toast.error("Please select required Add-On");
      setCustomerOpen(true);
      return;
    }

    const price =
      orderType === "dine_in"
        ? selectedMenuItem.pricing?.dine_in
        : orderType === "takeaway"
        ? selectedMenuItem.pricing?.takeaway
        : selectedMenuItem.pricing?.delivery || selectedMenuItem.price;

    // Calculate modifier prices
    const modifierPrice = Object.values(selectedModifiers).reduce(
      (sum, mod) => sum + (mod.option_price || 0),
      0
    );
    const addOnPrice = selectedAddOns.reduce(
      (sum, addon) => sum + addon.price,
      0
    );

    const totalPrice = price + modifierPrice + addOnPrice;

    const modifiersArray = Object.values(selectedModifiers);

    setCart([
      ...cart,
      {
        item_id: selectedMenuItem.id,
        item_name: selectedMenuItem.name,
        quantity: 1,
        price: totalPrice,
        tax: selectedMenuItem.tax || 0,
        notes: itemNoteForModifier,
        modifiers: modifiersArray,
        add_ons: selectedAddOns,
      },
    ]);

    toast.success(`${selectedMenuItem.name} added with customizations`);
    setModifierDialogOpen(false);
    setSelectedMenuItem(null);
  };

  const updateQuantity = (itemId, delta) => {
    setCart(
      cart.map((c) =>
        c.item_id === itemId
          ? { ...c, quantity: Math.max(1, c.quantity + delta) }
          : c
      )
    );
  };

  const removeFromCart = (itemId) => {
    console.log("testing cart items", cart);
    setCart(cart.filter((c) => c.item_id !== itemId));
  };

  const openNoteDialog = (item) => {
    setSelectedItemForNote(item);
    setItemNote(item.notes || "");
    setNoteDialogOpen(true);
  };

  const saveNote = () => {
    setCart(
      cart.map((c) =>
        c.item_id === selectedItemForNote.item_id
          ? { ...c, notes: itemNote }
          : c
      )
    );
    setNoteDialogOpen(false);
    setItemNote("");
    toast.success("Note added");
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => {
      const itemPrice = isSplitBill
        ? item.price * splitBillOrders.length
        : item.price;
      return sum + itemPrice * item.quantity;
    }, 0);

    const tax = cart.reduce((sum, item) => {
      const itemTax = isSplitBill
        ? item.tax * splitBillOrders.length
        : item.tax;
      return sum + itemTax * item.quantity;
    }, 0);

    const discountAmt =
      discountType === "percentage"
        ? (subtotal * discount) / 100
        : discountAmount;
    const total = subtotal + tax - discountAmt;
    return { subtotal, tax, discountAmt, total };
  };

  const applyDiscount = () => {
    setDiscountOpen(false);
    toast.success("Discount applied");
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    // Mandatory validations
    if (orderType === "dine_in" && !selectedTable) {
      toast.error("Please select a table for dine-in order");
      return;
    }

    if (
      (orderType === "takeaway" || orderType === "delivery") &&
      !customerName
    ) {
      toast.error("Please enter customer name");
      return;
    }

    if (
      (orderType === "takeaway" || orderType === "delivery") &&
      !customerPhone
    ) {
      toast.error("Please enter customer phone number");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/orders`,
        {
          table_id: orderType === "dine_in" ? selectedTable?.id : null,
          order_type: orderType,
          items: cart,
          customer_name: customerName || undefined,
          customer_phone: customerPhone || undefined,
        },
        { headers: getAuthHeader() }
      );

      // Set existing order ID so payment can be processed
      setExistingOrderId(response.data.id);
      setOrderPaymentStatus("pending"); // Order is pending payment

      // Print KOT for new order
      await axios.post(
        `${API}/kot/print?order_id=${response.data.id}`,
        {},
        {
          headers: getAuthHeader(),
        }
      );

      toast.success("Order placed and sent to kitchen! Ready for payment.");
      fetchTables();

      // Refresh table orders to show all orders including the new one
      if (orderType === "dine_in" && selectedTable) {
        await fetchTableOrders(selectedTable.id);
      }
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Failed to place order");
    }
  };

  const handleUpdateOrder = async () => {
    if (!existingOrderId) {
      toast.error("No existing order to update");
      return;
    }

    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    try {
      // Send update with new items only (we'll track this in state later)
      await axios.patch(
        `${API}/orders/${existingOrderId}/update`,
        {
          order_id: existingOrderId,
          new_items: cart, // For now sending all items, can be optimized
        },
        { headers: getAuthHeader() }
      );

      toast.success("Order updated and sent to kitchen!");
      resetOrder();
      fetchTables();
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Failed to update order");
    }
  };

  const resetOrder = () => {
    setCart([]);
    setCustomerName("");
    setCustomerPhone("");
    setSelectedTable(null);
    setDiscount(0);
    setDiscountAmount(0);
    setOrderTime(new Date());
    setElapsedTime(0);
    setExistingOrderId(null);
    setOrderPaymentStatus("pending");
  };

  const handleKOT = () => {
    toast.success("KOT sent to kitchen");
  };

  const handleGenerateBill = () => {
    toast.success("Bill generated");
  };

  const handlePayment = () => {
    setPaymentOpen(true);
  };

  const processPayment = async (paymentMethod) => {
    if (!existingOrderId) {
      toast.error("Please place order first");
      return;
    }

    try {
      // Mark payment as paid
      await axios.patch(
        `${API}/orders/${existingOrderId}/payment`,
        {},
        {
          params: { payment_method: paymentMethod },
          headers: getAuthHeader(),
        }
      );

      setOrderPaymentStatus("paid");

      // Auto-print receipt after payment
      try {
        await axios.post(
          `${API}/printers/print/receipt`,
          {
            order_id: existingOrderId,
          },
          { headers: getAuthHeader() }
        );
        toast.success(
          `Payment processed via ${paymentMethod}. Receipt sent to printer.`
        );
      } catch (printError) {
        console.error("Error printing receipt:", printError);
        toast.success(
          `Payment of $${calculateTotals().total.toFixed(
            2
          )} processed via ${paymentMethod}`
        );
        toast.warning("Receipt print failed. Use reprint button if needed.");
      }

      setPaymentOpen(false);
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error("Failed to process payment");
    }
  };

  const handleCompleteOrder = async () => {
    if (!existingOrderId) {
      toast.error("No order to complete");
      return;
    }

    try {
      // Update order status to completed
      await axios.patch(
        `${API}/orders/${existingOrderId}/status`,
        {
          order_status: "completed",
        },
        { headers: getAuthHeader() }
      );

      toast.success("Order completed! Table is now available.");
      resetOrder();
      fetchTables();
    } catch (error) {
      console.error("Error completing order:", error);
      toast.error("Failed to complete order");
    }
  };

  const reprintKOT = async () => {
    if (!existingOrderId) {
      toast.error("No order to print KOT for");
      return;
    }

    try {
      await axios.post(
        `${API}/printers/reprint/kot/${existingOrderId}`,
        {},
        {
          headers: getAuthHeader(),
        }
      );
      toast.success("KOT sent to printer");
    } catch (error) {
      console.error("Error reprinting KOT:", error);
      toast.error("Failed to print KOT");
    }
  };

  const reprintReceipt = async () => {
    if (!existingOrderId) {
      toast.error("No order to print receipt for");
      return;
    }

    try {
      await axios.post(
        `${API}/printers/reprint/receipt/${existingOrderId}`,
        {},
        {
          headers: getAuthHeader(),
        }
      );
      toast.success("Receipt sent to printer");
    } catch (error) {
      console.error("Error reprinting receipt:", error);
      toast.error("Failed to print receipt");
    }
  };

  const downloadKOTPDF = async () => {
    if (!existingOrderId) {
      toast.error("No order to download KOT for");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/orders/${existingOrderId}/kot-pdf`,
        {
          headers: getAuthHeader(),
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `KOT-${existingOrderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("KOT PDF downloaded");
    } catch (error) {
      console.error("Error downloading KOT PDF:", error);
      toast.error("Failed to download KOT PDF");
    }
  };

  const downloadReceiptPDF = async () => {
    if (!existingOrderId) {
      toast.error("No order to download receipt for");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/orders/${existingOrderId}/receipt-pdf`,
        {
          headers: getAuthHeader(),
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Receipt-${existingOrderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Receipt PDF downloaded");
    } catch (error) {
      console.error("Error downloading receipt PDF:", error);
      toast.error("Failed to download receipt PDF");
    }
  };

  const testNotification = () => {
    if (window.posNotifications && notificationsEnabled) {
      window.posNotifications.showNewOrderNotification({
        table_name: "Test Table 5",
        total: 45.99,
        items: [
          { quantity: 2, item_name: "Burger" },
          { quantity: 1, item_name: "Fries" },
          { quantity: 1, item_name: "Coke" },
        ],
      });
    } else {
      toast.error("Notifications are disabled or not loaded");
    }
  };

  const handleCancelOrder = async () => {
    if (!existingOrderId) {
      toast.error("No order to cancel");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to cancel this order? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      // Delete order and free table
      await axios.delete(`${API}/orders/${existingOrderId}`, {
        headers: getAuthHeader(),
      });

      toast.success("Order cancelled successfully. Table is now available.");
      resetOrder();
      fetchTables();
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast.error("Failed to cancel order");
    }
  };

  const showQR = (table) => {
    setSelectedQrTable(table);
    setQrDialogOpen(true);
  };

  const viewOrderDetails = (order) => {
    setOrderOpen(true);
    setSelectedOrderDetails(order);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const filteredMenuItems = menuItems.filter((item) => {
    const matchesCategory =
      selectedCategory === "all" || item.category_id === selectedCategory;
    const matchesSubCategory =
      selectedSubCategory === "all" ||
      item.sub_category_id === selectedSubCategory;
    const matchesSearch = item.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSubCategory && matchesSearch;
  });

  // Group items by category when "All Items" is selected
  const groupedMenuItems = () => {
    if (selectedCategory === "all") {
      // Create a map of category_id to category for sorting
      const categoryMap = {};
      categories.forEach((cat) => {
        categoryMap[cat.id] = cat;
      });

      // Group items by category
      const grouped = {};
      filteredMenuItems.forEach((item) => {
        if (!grouped[item.category_id]) {
          grouped[item.category_id] = [];
        }
        grouped[item.category_id].push(item);
      });

      // Sort categories by sort_order, then sort items within each category
      return categories
        .filter((cat) => grouped[cat.id] && grouped[cat.id].length > 0)
        .map((cat) => ({
          category: cat,
          items: grouped[cat.id].sort(
            (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
          ),
        }));
    }
    return null;
  };

  const { subtotal, tax, discountAmt, total } = calculateTotals();

  return (
    <POSLayout>
      <div className="flex flex-col h-screen bg-black overflow-hidden">
        {/* Top Navigation Bar - Keep existing POS design */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-3 md:px-6 py-2 md:py-3 shadow-lg flex justify-between items-center">
          <div className="flex items-center gap-2 md:gap-6">
            <h1 className="text-sm md:text-xl font-bold truncate max-w-[120px] md:max-w-none">{localStorage.getItem("restaurant") }</h1>
            {/* <select className="bg-white/20 text-white px-3 py-1 rounded border border-white/30 text-sm cursor-pointer">
              <option>Main Branch</option>
              <option>Branch 2</option>
            </select> */}
            <div className="text-xs md:text-sm hidden sm:block">{new Date().toLocaleString()}</div>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <Button
              variant="ghost"
              size="sm"
              className={`text-white hover:bg-white/20 relative ${
                !notificationsEnabled ? "opacity-50" : ""
              }`}
              onClick={() => {
                setNotificationsEnabled(!notificationsEnabled);
                if (!notificationsEnabled) {
                  toast.success("Notifications enabled");
                } else {
                  toast.info("Notifications disabled");
                }
              }}
              title={
                notificationsEnabled
                  ? "Notifications enabled - Click to disable"
                  : "Notifications disabled - Click to enable"
              }
            >
              <Bell size={18} />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {notificationCount}
                </span>
              )}
            </Button>
           <div className="hidden md:flex items-center gap-2 text-sm">
              <User size={18} />
              <span className="bg-white/20 text-white px-2 py-1 rounded border border-white/30">
                Admin User
              </span>
            </div>

            {/* Printer Status Indicators */}
            {printerStatus && (
              <div className="hidden lg:flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      printerStatus.kot_printer_online
                        ? "bg-green-500"
                        : "bg-red-500"
                    }`}
                  />
                  <span className="text-white/80">KOT</span>
                </div>
                <div className="flex items-center gap-1">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      printerStatus.receipt_printer_online
                        ? "bg-green-500"
                        : "bg-red-500"
                    }`}
                  />
                  <span className="text-white/80">Receipt</span>
                </div>
              </div>
            )}
            <Button
              onClick={() => navigate("/orders")}
              variant="outline"
              size="sm"
              className="text-white border-white hover:bg-white hover:text-red-600 flex items-center gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-3"
            >
              <Clock size={16} />
              <span className="hidden sm:inline">View Orders</span>
            </Button>
            <Button
              onClick={testNotification}
              variant="outline"
              size="sm"
              className="hidden md:flex text-white border-white hover:bg-white hover:text-red-600 items-center gap-2"
              title="Test notification sound and popup"
            >
              <Bell size={16} />
              Test Alert
            </Button>
            <Button
              onClick={() => {
                fetchTables();
                fetchMenuItems();
                fetchCategories();
                if (selectedTable) {
                  fetchTableOrders(selectedTable.id);
                }
                toast.success("Data refreshed!");
              }}
              variant="outline"
              size="sm"
              className="text-white border-white hover:bg-white hover:text-red-600 flex items-center gap-1 md:gap-2 px-2 md:px-3"
            >
              <RefreshCw size={16} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>

        {/* Mobile Bottom Navigation */}
        {isMobile && (
          <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 z-50 flex lg:hidden">
            <button
              onClick={() => setActivePanel('tables')}
              className={`flex-1 py-3 flex flex-col items-center gap-1 ${activePanel === 'tables' ? 'text-red-500 bg-gray-800' : 'text-gray-400'}`}
            >
              <LayoutGrid size={20} />
              <span className="text-xs">Tables</span>
            </button>
            <button
              onClick={() => setActivePanel('menu')}
              className={`flex-1 py-3 flex flex-col items-center gap-1 ${activePanel === 'menu' ? 'text-red-500 bg-gray-800' : 'text-gray-400'}`}
            >
              <Menu size={20} />
              <span className="text-xs">Menu</span>
            </button>
            <button
              onClick={() => setActivePanel('cart')}
              className={`flex-1 py-3 flex flex-col items-center gap-1 relative ${activePanel === 'cart' ? 'text-red-500 bg-gray-800' : 'text-gray-400'}`}
            >
              <ShoppingCart size={20} />
              <span className="text-xs">Cart</span>
              {cart.length > 0 && (
                <span className="absolute top-1 right-1/4 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {cart.length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className={`flex flex-1 overflow-hidden ${isMobile ? 'pb-16' : ''}`}>
          {/* LEFT - Tables (25%) */}
          <div className={`${isMobile ? (activePanel === 'tables' ? 'flex w-full' : 'hidden') : 'w-1/4'} bg-gray-900 border-r border-gray-700 flex-col`}>
            {/* Order Type Tabs */}
            <div className="grid grid-cols-2 gap-2 p-3 bg-black border-b border-gray-700">
              {[
                { type: "dine_in", label: "Dine-In" },
                { type: "takeaway", label: "Takeaway" },
                { type: "delivery", label: "Delivery" },
                { type: "online", label: "Online" },
              ].map(({ type, label }) => (
                <Button
                  key={type}
                  variant={orderType === type ? "default" : "outline"}
                  onClick={() => {
                    if (
                      (type === "takeaway" || type === "delivery") &&
                      !customerName
                    ) {
                      // Show customer details dialog immediately for takeaway/delivery
                      setOrderType(type);
                      setCustomerOpen(true);
                    } else {
                      setOrderType(type);
                    }
                  }}
                  className={
                    orderType === type
                      ? "bg-red-600 hover:bg-red-700 text-white"
                      : "bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700"
                  }
                  size="sm"
                >
                  {label}
                </Button>
              ))}
            </div>

            {/* Action Buttons for Tables */}
            {orderType === "dine_in" && (
              <div className="flex gap-2 p-3 bg-gray-900 border-b border-gray-700">
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-xs flex-1"
                >
                  <Plus size={14} className="mr-1" /> Add
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-gray-800 border-gray-700 text-gray-300 text-xs"
                >
                  <Edit2 size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-gray-800 border-gray-700 text-gray-300 text-xs"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            )}

            {/* Tables or Customer Info */}
            <div className="flex-1 overflow-y-auto p-3">
              {orderType === "dine_in" ? (
                <div className="space-y-3">
                  {/* Section Filters */}
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    <Button
                      size="sm"
                      variant={
                        selectedSection === "all" ? "default" : "outline"
                      }
                      className={`text-xs whitespace-nowrap ${
                        selectedSection === "all"
                          ? "bg-white text-black"
                          : "text-white border-white/30"
                      }`}
                      onClick={() => setSelectedSection("all")}
                    >
                      All
                    </Button>
                    {sections.map((section) => {
                      const colorMap = {
                        blue: "bg-blue-600",
                        green: "bg-green-600",
                        purple: "bg-purple-600",
                        orange: "bg-orange-600",
                        red: "bg-red-600",
                        gray: "bg-gray-600",
                      };
                      const bgColor = colorMap[section.color] || "bg-gray-600";

                      return (
                        <Button
                          key={section.id}
                          size="sm"
                          variant={
                            selectedSection === section.id
                              ? "default"
                              : "outline"
                          }
                          className={`text-xs whitespace-nowrap ${
                            selectedSection === section.id
                              ? `${bgColor} text-white`
                              : "text-white border-white/30"
                          }`}
                          onClick={() => setSelectedSection(section.id)}
                        >
                          {section.name}
                        </Button>
                      );
                    })}
                  </div>

                  {/* Table Search */}
                  <Input
                    placeholder="Search table number..."
                    value={tableSearchTerm}
                    onChange={(e) => setTableSearchTerm(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white text-xs"
                  />

                  {/* Status Legend */}
                  <div className="flex gap-2 text-xs flex-wrap">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-green-500 rounded"></div>
                      <span className="text-gray-400">Available</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                      <span className="text-gray-400">Occupied</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-red-500 rounded"></div>
                      <span className="text-gray-400">Reserved</span>
                    </div>
                  </div>

                  {/* Tables Grid - Responsive columns */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 gap-2">
                    {tables
                      .filter((table) => {
                        const matchesSection =
                          selectedSection === "all" ||
                          table.section_id === selectedSection;
                        const matchesSearch = table.table_name
                          .toLowerCase()
                          .includes(tableSearchTerm.toLowerCase());
                        return matchesSection && matchesSearch;
                      })
                      .map((table) => (
                        <Card
                          key={table.id}
                          className={`cursor-pointer border-2 transition-all hover:scale-105 ${
                            selectedTable?.id === table.id
                              ? "ring-2 ring-red-500 border-red-500"
                              : getTableStatusColor(table.status)
                          }`}
                          onClick={() => setSelectedTable(table)}
                        >
                          <CardContent className="p-2 text-center relative">
                            {/* Multiple Orders / Split Bill Indicator */}
                            {tablesWithSplitBills.has(table.id) && (
                              <div className="absolute -top-2 -left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg z-10 flex items-center gap-1">
                                <span>Multiple Orders</span>
                              </div>
                            )}
                            <div className="font-bold text-lg">
                              {table.table_name}
                            </div>
                            <div className="text-xs opacity-70">
                              {table.capacity}{" "}
                              <Utensils size={10} className="inline" />
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="absolute top-0 right-0 h-5 w-5 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                showQR(table);
                              }}
                            >
                              <QrCode size={12} />
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-gray-800 p-4 rounded border border-gray-700">
                    <h3 className="text-white font-semibold mb-3 text-sm">
                      Customer Details
                    </h3>
                    <div className="space-y-2">
                      <Input
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Customer name"
                        className="bg-gray-900 text-white border-gray-700"
                      />
                      <Input
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder="Phone number"
                        className="bg-gray-900 text-white border-gray-700"
                      />
                    </div>
                  </div>
                  {/* Active Orders List */}
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <h3 className="text-white font-semibold mb-2 text-sm">
                      Active Orders
                    </h3>
                    <div className="space-y-2 text-xs text-gray-400">
                      {activeOrdersLoading ? (
                        <div className="text-center py-2">Loading...</div>
                      ) : activeOrders.length === 0 ? (
                        <div className="text-center py-2">No active orders</div>
                      ) : (
                        activeOrders.map((order) => (
                          <div
                            key={order.id}
                            onClick={() => viewOrderDetails(order)}
                            className="flex cursor-pointer justify-between items-center bg-gray-900 p-2 rounded border border-gray-700"
                          >
                            <div>
                              <div className="text-white font-medium text-xs">
                                #{order.id.slice(-6)}
                              </div>
                              <div className="text-gray-400 text-[10px] capitalize">
                                {order.order_type.replace("_", " ")} •{" "}
                                {order.order_status}
                              </div>
                            </div>

                            <div className="text-right">
                              <div className="text-white text-xs font-semibold">
                                ${order.grand_total ?? order.total_amount}
                              </div>
                              <div className="text-gray-500 text-[10px]">
                                {new Date(
                                  order.created_at
                                ).toLocaleTimeString()}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* MIDDLE - Menu (45%) */}
          <div className={`${isMobile ? (activePanel === 'menu' ? 'flex w-full' : 'hidden') : 'flex-1'} bg-gray-100 flex-col`}>
            {/* Search and Filters */}
            <div className="p-2 sm:p-3 bg-white border-b flex flex-wrap sm:flex-nowrap gap-2 sm:gap-3">
              <div className="flex-1 relative min-w-[150px]">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search items..."
                  className="pl-10 text-sm"
                />
              </div>
              <Button variant="outline" size="sm" className="gap-1 sm:gap-2">
                <Filter size={16} />
                <span className="hidden sm:inline">Filter</span>
              </Button>
              <Button className="bg-red-600 hover:bg-red-700 gap-1 sm:gap-2" size="sm">
                <Plus size={16} />
                <span className="hidden sm:inline">Add Item</span>
              </Button>
            </div>

            {/* Category Tabs - Scrollable on mobile */}
            <div className="bg-white border-b px-2 sm:px-3 py-2 overflow-x-auto scrollbar-hide">
              <div className="flex gap-2 min-w-max">
                <Button
                  variant={selectedCategory === "all" ? "default" : "outline"}
                  onClick={() => setSelectedCategory("all")}
                  className={
                    selectedCategory === "all"
                      ? "bg-red-600 hover:bg-red-700"
                      : ""
                  }
                  size="sm"
                >
                  All Items
                </Button>
                {categories.map((cat) => (
                  <Button
                    key={cat.id}
                    variant={
                      selectedCategory === cat.id ? "default" : "outline"
                    }
                    onClick={() => setSelectedCategory(cat.id)}
                    className={
                      selectedCategory === cat.id
                        ? "bg-red-600 hover:bg-red-700"
                        : ""
                    }
                    size="sm"
                  >
                    {cat.name}
                  </Button>
                ))}
              </div>

              {/* Subcategory Filters */}
              {subCategories.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-3">
                  <Button
                    variant={
                      selectedSubCategory === "all" ? "default" : "outline"
                    }
                    onClick={() => setSelectedSubCategory("all")}
                    className={
                      selectedSubCategory === "all"
                        ? "bg-blue-600 hover:bg-blue-700"
                        : ""
                    }
                    size="sm"
                  >
                    All
                  </Button>
                  {subCategories.map((subCat) => (
                    <Button
                      key={subCat.id}
                      variant={
                        selectedSubCategory === subCat.id
                          ? "default"
                          : "outline"
                      }
                      onClick={() => setSelectedSubCategory(subCat.id)}
                      className={
                        selectedSubCategory === subCat.id
                          ? "bg-blue-600 hover:bg-blue-700"
                          : ""
                      }
                      size="sm"
                    >
                      {subCat.name}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Menu Items Grid */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
              {selectedCategory === "all" ? (
                /* Grouped by Category View */
                <div className="space-y-6">
                  {groupedMenuItems().map(({ category, items }) => (
                    <div key={category.id}>
                      {/* Category Header */}
                      <h3 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b-2 border-red-500">
                        {category.name}
                      </h3>
                      {/* Items Grid for this Category - Responsive */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                        {items.map((item) => (
                          <Card
                            key={item.id}
                            className="hover:shadow-md transition border border-gray-200 relative cursor-pointer"
                            onClick={() => item.availability && addToCart(item)}
                          >
                            <CardContent className="p-3">
                              {/* Availability Indicator */}
                              <div className="absolute top-2 right-2">
                                <div
                                  className={`w-2 h-2 rounded-full ${
                                    item.availability
                                      ? "bg-green-500"
                                      : "bg-red-500"
                                  }`}
                                />
                              </div>

                              <div className="font-semibold text-sm mb-1 pr-4">
                                {item.name}
                              </div>
                              <div className="text-xs text-gray-500 mb-2 uppercase tracking-wide">
                                {item.category_name || "Item"}
                              </div>
                              <div className="flex justify-between items-center mt-2">
                                <span className="text-base font-bold text-red-600">
                                  $
                                  {orderType === "dine_in"
                                    ? item.pricing?.dine_in
                                    : orderType === "takeaway"
                                    ? item.pricing?.takeaway
                                    : item.pricing?.delivery || item.price}
                                </span>
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addToCart(item);
                                  }}
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700 h-7 w-7 p-0"
                                  disabled={!item.availability}
                                >
                                  <Plus size={14} />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Single Category View - Responsive */
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                  {filteredMenuItems.map((item) => (
                    <Card
                      key={item.id}
                      className="hover:shadow-md transition border border-gray-200 relative cursor-pointer"
                      onClick={() => item.availability && addToCart(item)}
                    >
                      <CardContent className="p-3">
                        {/* Availability Indicator */}
                        <div className="absolute top-2 right-2">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              item.availability ? "bg-green-500" : "bg-red-500"
                            }`}
                          />
                        </div>

                        <div className="font-semibold text-sm mb-1 pr-4">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500 mb-2 uppercase tracking-wide">
                          {item.category_name || "Item"}
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-base font-bold text-red-600">
                            $
                            {orderType === "dine_in"
                              ? item.pricing?.dine_in
                              : orderType === "takeaway"
                              ? item.pricing?.takeaway
                              : item.pricing?.delivery || item.price}
                          </span>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(item);
                            }}
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 h-7 w-7 p-0"
                            disabled={!item.availability}
                          >
                            <Plus size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT - Cart (28%) */}
          <div className={`${isMobile ? (activePanel === 'cart' ? 'flex w-full' : 'hidden') : 'w-[28%]'} bg-white border-l border-gray-300 flex-col min-w-0`}>
            {/* Order Header */}
            <div className="p-3 border-b bg-gray-50">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h2 className="text-sm font-bold flex items-center gap-1 min-w-0 flex-1">
                  <ShoppingCart size={18} className="flex-shrink-0" />
                  <span className="truncate">
                    {orderType === "dine_in"
                      ? "Dine-In Order"
                      : orderType === "takeaway"
                      ? "Takeaway Order"
                      : "Delivery Order"}
                  </span>
                </h2>
                {cart.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (window.confirm("Clear all items from cart?")) {
                        setCart([]);
                        // Don't clear navigation if there are multiple orders or split persons
                        if (
                          allTableOrders.length <= 1 &&
                          (!allTableOrders[0]?.isSplitBill ||
                            splitBillOrders.length <= 1)
                        ) {
                          setIsSplitBill(false);
                          setSplitBillOrders([]);
                          setCurrentSplitPersonIndex(0);
                        }
                      }
                    }}
                    className="text-red-600 border-red-600 hover:bg-red-50 text-xs px-2 py-1 flex-shrink-0 whitespace-nowrap"
                  >
                    <X size={14} className="mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
              {selectedTable && (
                <div className="text-sm font-semibold text-gray-700">
                  Table: {selectedTable.table_name}
                </div>
              )}
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <div className="flex items-center gap-1">
                  <User size={12} />
                  Server: {serverName}
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={12} />
                  Time: {formatTime(elapsedTime)}
                </div>
              </div>
            </div>

            {/* Two-Level Order Navigation */}
            {allTableOrders.length > 0 && (
              <>
                {/* Top Level: Navigate between different orders */}
                {allTableOrders.length > 1 && (
                  <div className="border-b bg-purple-50 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-bold text-purple-800 flex items-center gap-1">
                        <span>📋</span>
                        Table Orders
                      </h3>
                      <span className="text-xs text-purple-600 font-semibold">
                        Order {currentOrderIndex + 1} of {allTableOrders.length}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (currentOrderIndex > 0) {
                            const newIndex = currentOrderIndex - 1;
                            setCurrentOrderIndex(newIndex);
                            setCurrentSplitPersonIndex(0);
                            loadOrderGroup(allTableOrders[newIndex], newIndex);
                          }
                        }}
                        disabled={currentOrderIndex === 0}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronLeft size={16} />
                      </Button>

                      <div className="flex-1 bg-white rounded border border-purple-200 p-2">
                        <div className="text-sm font-semibold text-gray-800">
                          {allTableOrders[currentOrderIndex].isSplitBill
                            ? `Split Bill (${allTableOrders[currentOrderIndex].orders.length} persons)`
                            : `Regular Order`}
                        </div>
                        <div className="text-xs text-gray-600">
                          {allTableOrders[currentOrderIndex].orders[0]
                            ?.customer_name ||
                            allTableOrders[currentOrderIndex].orders[0]
                              ?.split_person_name ||
                            "Guest"}
                        </div>
                        <div className="text-xs text-purple-600 font-semibold mt-1">
                          Order ID:{" "}
                          {allTableOrders[
                            currentOrderIndex
                          ].orders[0]?.id?.slice(-8)}
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (currentOrderIndex < allTableOrders.length - 1) {
                            const newIndex = currentOrderIndex + 1;
                            setCurrentOrderIndex(newIndex);
                            setCurrentSplitPersonIndex(0);
                            loadOrderGroup(allTableOrders[newIndex], newIndex);
                          }
                        }}
                        disabled={
                          currentOrderIndex === allTableOrders.length - 1
                        }
                        className="h-8 w-8 p-0"
                      >
                        <ChevronRight size={16} />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Second Level: Navigate within split bill (if current order is split) */}
                {allTableOrders[currentOrderIndex]?.isSplitBill &&
                  splitBillOrders.length > 1 && (
                    <div className="border-b bg-blue-50 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-bold text-blue-800 flex items-center gap-1">
                          <span>🔀</span>
                          Split Bill Persons
                        </h3>
                        <span className="text-xs text-blue-600 font-semibold">
                          Person {currentSplitPersonIndex + 1} of{" "}
                          {splitBillOrders.length}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (currentSplitPersonIndex > 0) {
                              const newIndex = currentSplitPersonIndex - 1;
                              setCurrentSplitPersonIndex(newIndex);
                              const order = splitBillOrders[newIndex];
                              setCart(order.items || []);
                              setCustomerName(
                                order?.customer_name ||
                                  order.split_person_name ||
                                  ""
                              );
                              setCustomerPhone(
                                order.customer_phone ||
                                  order.split_person_phone ||
                                  ""
                              );
                              setExistingOrderId(order.id);
                              setOrderPaymentStatus(
                                order.payment_status || "pending"
                              );
                            }
                          }}
                          disabled={currentSplitPersonIndex === 0}
                          className="h-8 w-8 p-0"
                        >
                          <ChevronLeft size={16} />
                        </Button>

                        <div className="flex-1 bg-white rounded border border-blue-200 p-2">
                          <div className="text-sm font-semibold text-gray-800">
                            {splitBillOrders[currentSplitPersonIndex]
                              ?.split_person_name ||
                              splitBillOrders[currentSplitPersonIndex]
                                ?.customer_name ||
                              "Guest"}
                          </div>
                          <div className="text-xs text-gray-600">
                            {splitBillOrders[currentSplitPersonIndex]
                              ?.split_person_phone ||
                              splitBillOrders[currentSplitPersonIndex]
                                ?.customer_phone ||
                              ""}
                          </div>
                          <div className="text-xs text-blue-600 font-semibold mt-1">
                            Total: $
                            {splitBillOrders[
                              currentSplitPersonIndex
                            ]?.grand_total?.toFixed(2) || "0.00"}
                          </div>
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (
                              currentSplitPersonIndex <
                              splitBillOrders.length - 1
                            ) {
                              const newIndex = currentSplitPersonIndex + 1;
                              setCurrentSplitPersonIndex(newIndex);
                              const order = splitBillOrders[newIndex];
                              setCart(order.items || []);
                              setCustomerName(
                                order?.customer_name ||
                                  order.split_person_name ||
                                  ""
                              );
                              setCustomerPhone(
                                order.customer_phone ||
                                  order.split_person_phone ||
                                  ""
                              );
                              setExistingOrderId(order.id);
                              setOrderPaymentStatus(
                                order.payment_status || "pending"
                              );
                            }
                          }}
                          disabled={
                            currentSplitPersonIndex ===
                            splitBillOrders.length - 1
                          }
                          className="h-8 w-8 p-0"
                        >
                          <ChevronRight size={16} />
                        </Button>
                      </div>
                    </div>
                  )}
              </>
            )}

            {/* Order Items */}
            <div className="flex-1 overflow-y-auto p-3">
              {cart.length === 0 ? (
                <div className="text-center text-gray-400 py-16">
                  <ShoppingCart size={48} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No items in cart</p>
                  <p className="text-xs mt-1">Start adding items from menu</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cart.map((item, index) => (
                    <div
                      key={`${item.item_id}-${index}`}
                      className="bg-gray-50 p-3 rounded border border-gray-200"
                    >
                      <div className="flex justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-semibold text-sm">
                            {item.item_name}
                          </div>

                          {/* Show Modifiers */}
                          {item.modifiers && item.modifiers.length > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {item.modifiers.map((mod, idx) => (
                                <div key={idx} className="flex justify-between">
                                  <span>
                                    • {mod.group_name}: {mod.option_name}
                                  </span>
                                  {mod.option_price > 0 && (
                                    <span className="text-green-600">
                                      +${mod.option_price.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Show Add-ons */}
                          {item.add_ons && item.add_ons.length > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {item.add_ons.map((addon, idx) => (
                                <div key={idx} className="flex justify-between">
                                  <span>+ {addon.name}</span>
                                  <span className="text-green-600">
                                    +${addon.price.toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                          {item.notes && (
                            <div className="text-xs text-gray-600 italic mt-1 bg-yellow-50 p-1 rounded">
                              📝 {item.notes}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFromCart(item.item_id)}
                          className="h-6 w-6 p-0"
                        >
                          <Trash2 size={14} className="text-red-600" />
                        </Button>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(item.item_id, -1)}
                            className="h-7 w-7 p-0"
                          >
                            <Minus size={12} />
                          </Button>
                          <span className="w-8 text-center font-bold text-sm">
                            {item.quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(item.item_id, 1)}
                            className="h-7 w-7 p-0"
                          >
                            <Plus size={12} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openNoteDialog(item)}
                            className="h-7 px-2 text-xs ml-1"
                          >
                            📝
                          </Button>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-sm">
                            $
                            {(isSplitBill
                              ? item.price *
                                splitBillOrders.length *
                                item.quantity
                              : item.price * item.quantity
                            ).toFixed(2)}
                          </span>
                          {isSplitBill && (
                            <div className="text-xs text-blue-600 mt-0.5">
                              Split: ${(item.price * item.quantity).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Billing Summary & Actions */}
              {cart.length > 0 && (
                <div className="border-t p-4 bg-gray-50 space-y-3 mt-4">
                  {/* Discount & Promo Buttons */}
                  <div className="flex gap-2">
                    <Dialog open={discountOpen} onOpenChange={setDiscountOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="flex-1 text-xs">
                          Apply Discount
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Apply Discount</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label>Discount Type</Label>
                            <select
                              className="w-full p-2 border rounded"
                              value={discountType}
                              onChange={(e) => setDiscountType(e.target.value)}
                            >
                              <option value="percentage">Percentage (%)</option>
                              <option value="fixed">Fixed Amount ($)</option>
                            </select>
                          </div>
                          {discountType === "percentage" ? (
                            <div>
                              <Label>Discount Percentage</Label>
                              <Input
                                type="number"
                                value={discount}
                                onChange={(e) =>
                                  setDiscount(
                                    Math.max(
                                      0,
                                      Math.min(100, Number(e.target.value))
                                    )
                                  )
                                }
                                placeholder="0"
                              />
                            </div>
                          ) : (
                            <div>
                              <Label>Discount Amount ($)</Label>
                              <Input
                                type="number"
                                value={discountAmount}
                                onChange={(e) =>
                                  setDiscountAmount(
                                    Math.max(0, Number(e.target.value))
                                  )
                                }
                                placeholder="0.00"
                              />
                            </div>
                          )}
                          <Button
                            onClick={applyDiscount}
                            className="w-full bg-red-600"
                          >
                            Apply
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button variant="outline" className="flex-1 text-xs">
                      Add Promo
                    </Button>
                  </div>

                  {/* Billing Summary */}
                  <div className="space-y-1 text-sm border-t pt-3">
                    <div className="flex justify-between gap-2">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold">
                        ${subtotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-gray-600">Tax:</span>
                      <span className="font-semibold">${tax.toFixed(2)}</span>
                    </div>
                    {discountAmt > 0 && (
                      <div className="flex justify-between gap-2 text-red-600">
                        <span>Discount:</span>
                        <span className="font-semibold">
                          -${discountAmt.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between gap-2 text-lg font-bold border-t pt-2 mt-2">
                      <span className="truncate">GRAND TOTAL:</span>
                      <span className="text-red-600 flex-shrink-0">${total.toFixed(2)}</span>
                    </div>
                    {isSplitBill && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                        <div className="text-xs text-blue-700 font-semibold mb-1">
                          Split Bill - This Person's Share:
                        </div>
                        <div className="flex justify-between gap-2 text-sm">
                          <span className="text-blue-600">Subtotal:</span>
                          <span className="font-semibold text-blue-600">
                            ${(subtotal / splitBillOrders.length).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2 text-sm">
                          <span className="text-blue-600">Tax:</span>
                          <span className="font-semibold text-blue-600">
                            ${(tax / splitBillOrders.length).toFixed(2)}
                          </span>
                        </div>
                        {discountAmt > 0 && (
                          <div className="flex justify-between gap-2 text-sm text-red-600">
                            <span>Discount:</span>
                            <span className="font-semibold">
                              -$
                              {(discountAmt / splitBillOrders.length).toFixed(
                                2
                              )}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between gap-2 text-sm font-bold border-t border-blue-200 pt-1 mt-1">
                          <span className="text-blue-700 truncate">Share Total:</span>
                          <span className="text-blue-700 flex-shrink-0">
                            ${(total / splitBillOrders.length).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-1">
                    {/* New Order Button - shows when there's an existing order to allow adding another order to same table */}
                    {existingOrderId && selectedTable && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Clear cart to start a new order for the same table
                          setCart([]);
                          setCustomerName("");
                          setCustomerPhone("");
                          setExistingOrderId(null);
                          setOrderPaymentStatus("pending");
                          setIsSplitBill(false);
                          setSplitBillOrders([]);
                          setCurrentSplitPersonIndex(0);
                          // Keep allTableOrders to maintain top-level navigation
                          toast.info(
                            "Starting new order for " + selectedTable.table_name
                          );
                        }}
                        className="text-xs gap-1 col-span-2 bg-purple-600 text-white hover:bg-purple-700 truncate"
                        title="New Order (Same Table)"
                      >
                        <Plus size={14} className="flex-shrink-0" />
                        <span className="truncate">New Order</span>
                      </Button>
                    )}

                    {/* Show Place/Update Order based on whether order exists and cart has items */}
                    {cart.length > 0 && (
                      <Button
                        variant="outline"
                        onClick={
                          existingOrderId ? handleUpdateOrder : handlePlaceOrder
                        }
                        className="text-xs gap-1 col-span-2 bg-red-600 text-white hover:bg-red-700 truncate"
                      >
                        <Save size={14} className="flex-shrink-0" />
                        <span className="truncate">{existingOrderId ? "Update Order" : "Place order"}</span>
                      </Button>
                    )}

                    {/* Cancel Order Button - shows for existing orders */}
                    {existingOrderId && (
                      <Button
                        variant="outline"
                        onClick={handleCancelOrder}
                        className="text-xs gap-1 col-span-2 bg-gray-600 text-white hover:bg-gray-700 truncate"
                      >
                        <X size={14} className="flex-shrink-0" />
                        <span className="truncate">Cancel Order</span>
                      </Button>
                    )}

                    {/* Complete Order Button - shows only AFTER payment is done */}
                    {existingOrderId && orderPaymentStatus === "paid" && (
                      <Button
                        variant="outline"
                        onClick={handleCompleteOrder}
                        className="text-xs gap-1 col-span-2 bg-green-600 text-white hover:bg-green-700 truncate"
                        title="Complete Order & Free Table"
                      >
                        <span>✓</span>
                        <span className="truncate">Complete Order & Free Table</span>
                      </Button>
                    )}

                    {/* Reprint Buttons - show when order exists */}
                    {existingOrderId && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => handleReprintKot(existingOrderId)}
                          disabled={reprintingKot}
                          className="text-xs gap-1 bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 truncate"
                          title="Reprint KOT"
                        >
                          <Printer size={14} className="flex-shrink-0" />
                          <span className="truncate">{reprintingKot ? "Printing..." : "Reprint KOT"}</span>
                        </Button>
                        <Button
                          variant="outline"
                          onClick={downloadKOTPDF}
                          className="text-xs gap-1 bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-300 truncate"
                          title="Download KOT"
                        >
                          <Download size={14} className="flex-shrink-0" />
                          <span className="truncate">Download KOT</span>
                        </Button>
                        {orderPaymentStatus === "paid" && (
                          <>
                            <Button
                              variant="outline"
                              onClick={() =>
                                handleReprintReceipt(existingOrderId)
                              }
                              disabled={reprintingReceipt}
                              className="text-xs gap-1 bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 truncate"
                              title="Reprint Receipt"
                            >
                              <Printer size={14} className="flex-shrink-0" />
                              <span className="truncate">{reprintingReceipt ? "Printing..." : "Reprint Receipt"}</span>
                            </Button>
                            <Button
                              variant="outline"
                              onClick={downloadReceiptPDF}
                              className="text-xs gap-1 bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-300 truncate"
                              title="Download Receipt"
                            >
                              <Download size={14} className="flex-shrink-0" />
                              <span className="truncate">Download Receipt</span>
                            </Button>
                          </>
                        )}
                      </>
                    )}

                    {/* Payment Button - show only if order exists and payment is pending */}
                    {existingOrderId && orderPaymentStatus === "pending" && (
                      <Button
                        variant="outline"
                        onClick={() => setPaymentOpen(true)}
                        className="text-xs gap-1 col-span-2 bg-blue-600 text-white hover:bg-blue-700"
                      >
                        <DollarSign size={14} />
                        {isSplitBill ? (
                          <>
                            Payment - Split Share ($
                            {(total / splitBillOrders.length).toFixed(2)})
                          </>
                        ) : (
                          <>Payment (${total.toFixed(2)})</>
                        )}
                      </Button>
                    )}

                    <Button
                      variant="outline"
                      onClick={handleKOT}
                      className="text-xs gap-1"
                      disabled
                    >
                      <Printer size={14} />
                      KOT
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleGenerateBill}
                      className="text-xs gap-1"
                      disabled
                    >
                      <DollarSign size={14} />
                      Bill
                    </Button>
                  </div>

                  {/* Add Customer Button */}
                  {(orderType === "takeaway" || orderType === "delivery") && (
                    <Dialog
                      open={customerOpen}
                      onOpenChange={(open) => {
                        // Don't allow closing if fields are empty and order type is takeaway/delivery
                        if (
                          !open &&
                          (orderType === "takeaway" || orderType === "delivery")
                        ) {
                          if (!customerName || !customerPhone) {
                            toast.error(
                              "Customer details are required for " +
                                (orderType === "takeaway"
                                  ? "Takeaway"
                                  : "Delivery") +
                                " orders"
                            );
                            return;
                          }
                        }
                        setCustomerOpen(open);
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full text-xs">
                          🧍‍♂️ {customerName ? "Edit" : "Add"} Customer Info
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            {orderType === "takeaway"
                              ? "📦 Takeaway"
                              : orderType === "delivery"
                              ? "🛵 Delivery"
                              : ""}{" "}
                            Customer Information
                          </DialogTitle>
                          <p className="text-sm text-gray-500 mt-2">
                            Please enter customer details to proceed
                          </p>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label className="text-red-600">
                              Customer Name *
                            </Label>
                            <Input
                              value={customerName}
                              onChange={(e) => setCustomerName(e.target.value)}
                              placeholder="Enter customer name"
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-red-600">
                              Phone Number *
                            </Label>
                            <Input
                              value={customerPhone}
                              onChange={(e) => setCustomerPhone(e.target.value)}
                              placeholder="Enter phone number"
                              type="tel"
                              required
                              className="mt-1"
                            />
                          </div>
                          <Button
                            onClick={() => {
                              if (!customerName || !customerPhone) {
                                toast.error(
                                  "Both customer name and phone number are required"
                                );
                                return;
                              }
                              setCustomerOpen(false);
                              toast.success(
                                "Customer details saved. You can now add items to the order."
                              );
                            }}
                            className="w-full bg-red-600 hover:bg-red-700"
                            disabled={!customerName || !customerPhone}
                          >
                            Save & Continue
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              )}
              {/* Order details */}
              <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
                <DialogContent className="z-50 max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Order Details</DialogTitle>
                  </DialogHeader>

                  {/* Customer Info */}
                  <div className="border-b pb-3">
                    <p className="text-sm text-gray-500">Customer</p>
                    <p className="font-semibold">
                      {selectedOrderDetails?.customer_name}
                    </p>
                    <p className="text-sm">
                      {selectedOrderDetails?.customer_phone}
                    </p>
                  </div>

                  {/* Order Info */}
                  <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                    <div>
                      <p className="text-gray-500">Order ID</p>
                      <p className="font-medium">{selectedOrderDetails?.id}</p>
                    </div>

                    <div>
                      <p className="text-gray-500">Order Type</p>
                      <p className="font-medium capitalize">
                        {selectedOrderDetails?.order_type}
                      </p>
                    </div>

                    <div>
                      <p className="text-gray-500">Order Status</p>
                      <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-700 text-xs">
                        {selectedOrderDetails?.order_status}
                      </span>
                    </div>

                    <div>
                      <p className="text-gray-500">Payment Status</p>
                      <span className="px-2 py-1 rounded bg-red-100 text-red-600 text-xs">
                        {selectedOrderDetails?.payment_status}
                      </span>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="mt-4">
                    <p className="text-sm font-semibold mb-2">Items</p>

                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedOrderDetails?.items?.map((item, index) => (
                        <div
                          key={index}
                          className="flex justify-between text-sm border-b pb-1"
                        >
                          <span>
                            {item?.item_name} × {item?.quantity}
                          </span>
                          <span>${item?.total_price ?? item?.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Amount Summary */}
                  <div className="border-t mt-4 pt-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span>${selectedOrderDetails?.total_amount}</span>
                    </div>

                    <div className="flex justify-between">
                      <span>Tax</span>
                      <span>${selectedOrderDetails?.tax}</span>
                    </div>

                    <div className="flex justify-between font-bold text-base">
                      <span>Grand Total</span>
                      <span>${selectedOrderDetails?.grand_total}</span>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-black text-white px-6 py-2 text-xs flex justify-between border-t border-gray-800">
          <div className="flex items-center gap-4">
            <span>WaiterMan POS v1.0</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Online</span>
            </div>
            <div className="flex items-center gap-2">
              <Send size={12} />
              <span>Last sync: {new Date().toLocaleTimeString()}</span>
            </div>
          </div>
        </div>

        {/* Dialogs */}
        <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Select Payment Method</DialogTitle>
              {allTableOrders.length > 1 && (
                <p className="text-sm text-gray-600 mt-2">
                  Processing payment for Order {currentOrderIndex + 1} of{" "}
                  {allTableOrders.length}
                  {allTableOrders[currentOrderIndex]?.isSplitBill &&
                    ` - Person ${currentSplitPersonIndex + 1} of ${
                      splitBillOrders.length
                    }`}
                </p>
              )}
              {allTableOrders.length === 1 &&
                allTableOrders[0]?.isSplitBill &&
                splitBillOrders.length > 1 && (
                  <p className="text-sm text-gray-600 mt-2">
                    Processing payment for Person {currentSplitPersonIndex + 1}{" "}
                    of {splitBillOrders.length}
                  </p>
                )}
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              {[
                { method: "Cash", icon: "💵", color: "bg-green-600" },
                { method: "Card", icon: "💳", color: "bg-blue-600" },
                { method: "UPI", icon: "📱", color: "bg-purple-600" },
                { method: "Split", icon: "🔀", color: "bg-orange-600" },
              ].map(({ method, icon, color }) => {
                const isDisabled = method === "Split" && isSplitBill;
                return (
                  <Button
                    key={method}
                    onClick={() => processPayment(method)}
                    disabled={isDisabled}
                    className={`h-24 text-lg ${color} hover:opacity-90 ${
                      isDisabled ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    title={isDisabled ? "Already a split bill order" : ""}
                  >
                    {icon} {method}
                    {isDisabled && (
                      <span className="block text-xs mt-1">
                        (Already Split)
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Special Instructions</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label>Kitchen Notes for {selectedItemForNote?.item_name}</Label>
              <Textarea
                value={itemNote}
                onChange={(e) => setItemNote(e.target.value)}
                placeholder="E.g., no onions, extra spicy, well done..."
                rows={4}
                className="mt-2"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setNoteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={saveNote} className="bg-red-600">
                Save Note
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                QR Code for {selectedQrTable?.table_name}
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center py-4">
              {selectedQrTable?.qr_url ? (
                <>
                  <img
                    src={selectedQrTable.qr_url}
                    alt="QR Code"
                    className="w-64 h-64"
                  />
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    Customers can scan this QR code to place orders directly
                  </p>
                  <div className="flex gap-2 mt-4">
                    <Button
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => {
                        const url = `${BACKEND_URL}/order/${selectedQrTable.id}`;

                        const copyFallback = (text) => {
                          const textArea = document.createElement("textarea");
                          textArea.value = text;
                          textArea.style.position = "fixed";
                          textArea.style.left = "-9999px";
                          document.body.appendChild(textArea);
                          textArea.select();
                          try {
                            document.execCommand("copy");
                            toast.success("URL copied to clipboard!");
                          } catch (err) {
                            toast.error("Failed to copy URL");
                          }
                          document.body.removeChild(textArea);
                        };

                        // Use clipboard API if available, otherwise fallback to execCommand
                        if (
                          navigator.clipboard &&
                          navigator.clipboard.writeText
                        ) {
                          navigator.clipboard
                            .writeText(url)
                            .then(() => {
                              toast.success("URL copied to clipboard!");
                            })
                            .catch(() => {
                              // Fallback for clipboard API failure
                              copyFallback(url);
                            });
                        } else {
                          copyFallback(url);
                        }
                      }}
                    >
                      📋 Copy URL
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={async () => {
                        try {
                          const response = await axios.get(
                            `${API}/tables/${selectedQrTable.id}/qr`,
                            getAuthHeader()
                          );
                          const blob = new Blob([response.data], {
                            type: "image/png",
                          });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement("a");
                          link.href = selectedQrTable.qr_url;
                          link.download = `QR-Table-${selectedQrTable.table_name}.png`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          toast.success("QR Code downloaded!");
                        } catch (error) {
                          // Fallback - try to download from base64
                          const link = document.createElement("a");
                          link.href = selectedQrTable.qr_url;
                          link.download = `QR-Table-${selectedQrTable.table_name}.png`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          toast.success("QR Code downloaded!");
                        }
                      }}
                    >
                      <Download size={16} className="mr-2" />
                      Download QR
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-gray-500">
                  No QR code available for this table
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Modifier Dialog */}
        <ModifierDialog
          open={modifierDialogOpen}
          onOpenChange={setModifierDialogOpen}
          menuItem={selectedMenuItem}
          selectedModifiers={selectedModifiers}
          setSelectedModifiers={setSelectedModifiers}
          selectedAddOns={selectedAddOns}
          setSelectedAddOns={setSelectedAddOns}
          itemNote={itemNoteForModifier}
          setItemNote={setItemNoteForModifier}
          onAdd={addToCartWithModifiers}
        />
      </div>
    </POSLayout>
  );
}
